import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Buffer from './Buffer';
import Wheel from './Wheel';
import Keys from './Keys';
import Globe from './Globe';
import { heapMB, useFps } from '../editor/telemetry';
import { TREE, OPENABLE, STATS, findBuffer } from '../editor/buffers';
import { THEMES } from '../editor/themes';
import { CONTACT, SOCIALS } from '../constants';

const COMMANDS = ['e', 'theme', 'themes', 'set', 'neofetch', 'help', 'q', 'contact'];

// Complete the command name, or the argument of :e / :theme.
function complete(input) {
    const m = input.match(/^(\S*)(\s+)?(.*)$/);
    const [, name, space, arg] = m;
    if (!space) {
        const hit = COMMANDS.find((c) => c.startsWith(name));
        return hit ? `${hit} ` : input;
    }
    const pool = ['set', 'se'].includes(name)
        ? ['rnu', 'nornu', 'rnu!', 'crt', 'nocrt', 'crt!']
        : ['e', 'edit', 'o', 'open'].includes(name)
        ? OPENABLE.flatMap((b) => [b.path, b.name.replace(/\.\w+$/, '')])
        : ['theme', 'colo', 'colorscheme'].includes(name)
          ? THEMES.map((t) => t.name)
          : [];
    const hit = pool.find((p) => p.toLowerCase().startsWith(arg.toLowerCase()) && p !== arg);
    return hit ? `${name} ${hit}` : input;
}

const FILETYPES = { md: 'markdown', json: 'json', term: 'zsh' };

function useAbujaTime() {
    const format = () =>
        new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit' }).format(new Date());
    const [time, setTime] = useState(format);
    useEffect(() => {
        const id = setInterval(() => setTime(format()), 15000);
        return () => clearInterval(id);
    }, []);
    return time;
}

function glyph(name) {
    if (name.endsWith('.json')) return '{}';
    if (name.endsWith('.txt')) return '¶';
    return '#';
}

function position(el) {
    if (!el) return 'Top';
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 4) return 'All';
    if (el.scrollTop <= 2) return 'Top';
    if (el.scrollTop >= max - 2) return 'Bot';
    return `${Math.round((el.scrollTop / max) * 100)}%`;
}

function Tree({ current, onOpen }) {
    return (
        <ul className="tree-list" role="listbox" aria-label="Files">
            {TREE.map((node, i) =>
                node.type === 'dir' ? (
                    <li key={i} className="tree-dir" style={{ '--depth': node.depth || 0 }} aria-hidden="true">
                        <span className="tree-caret">▾</span> {node.name}
                    </li>
                ) : (
                    <li key={node.buffer.path} role="presentation">
                        <button
                            type="button"
                            role="option"
                            aria-selected={node.buffer === current}
                            className="tree-file"
                            style={{ '--depth': node.depth }}
                            onClick={() => onOpen(node.buffer)}
                        >
                            <span className={`tree-glyph ${node.buffer.lang}`}>{glyph(node.buffer.name)}</span>
                            <span className="tree-name">{node.buffer.name}</span>
                        </button>
                    </li>
                )
            )}
        </ul>
    );
}

// Live frame rate with a sparkline, plus JS heap where the browser exposes it.
function Perf() {
    const { fps, history } = useFps();
    const heap = heapMB();
    const W = 96;
    const H = 14;
    const points = history
        .map((v, i) => `${((i / Math.max(1, history.length - 1)) * W).toFixed(1)},${(H - (Math.min(v, 120) / 120) * H).toFixed(1)}`)
        .join(' ');
    return (
        <p className="col hide-sm perf">
            <span className="label">
                FPS{heap != null && ` · HEAP ${heap}MB`}
            </span>
            <span className="perf-row">
                <span className="value">{fps ?? '—'}</span>
                <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
                    <line x1="0" x2={W} y1={H / 2} y2={H / 2} className="perf-mid" />
                    {history.length > 1 && <polyline points={points} className="perf-line" />}
                </svg>
            </span>
        </p>
    );
}

export default function Editor({ buffer, theme, onOpen, onTheme, onQuit }) {
    const bufferRef = useRef(null);
    const inputRef = useRef(null);
    const pendingG = useRef(0);
    const history = useRef([]);
    const histAt = useRef(null);
    const [cmd, setCmd] = useState(null); // null = normal mode, string = command line text
    const [message, setMessage] = useState('');
    const [pos, setPos] = useState('Top');
    const [treeOpen, setTreeOpen] = useState(false);
    const [opts, setOpts] = useState({ rnu: false, crt: false });
    const openedAt = useRef(performance.now());
    const time = useAbujaTime();
    const wheelIndex = Math.max(0, OPENABLE.indexOf(buffer));

    useEffect(() => {
        const el = bufferRef.current;
        if (el) el.scrollTop = 0;
        setPos(position(el));
        setTreeOpen(false);
        document.title = `${buffer.name} — Adeboye Bello`;
    }, [buffer]);

    useEffect(() => {
        if (cmd !== null) inputRef.current?.focus();
    }, [cmd]);

    // Vim-style file info once the new buffer is on screen: "path" 34L, 2.1KB [rendered 3.2ms]
    useLayoutEffect(() => {
        const text = buffer.lines.filter((l) => typeof l === 'string').join('\n');
        const bytes = new Blob([text]).size;
        const size = bytes > 1024 ? `${(bytes / 1024).toFixed(1)}KB` : `${bytes}B`;
        const raf = requestAnimationFrame(() => {
            const ms = performance.now() - openedAt.current;
            setMessage(`"${buffer.path}" ${buffer.lines.length}L, ${size}  [rendered ${ms.toFixed(1)}ms]`);
        });
        return () => cancelAnimationFrame(raf);
    }, [buffer]);

    const open = useCallback(
        (b) => {
            setMessage('');
            openedAt.current = performance.now();
            onOpen(b);
        },
        [onOpen]
    );

    const step = useCallback(
        (delta) => {
            const i = OPENABLE.indexOf(buffer);
            const next = OPENABLE[(i + delta + OPENABLE.length) % OPENABLE.length];
            open(next);
        },
        [buffer, open]
    );

    const selectIndex = useCallback((i) => open(OPENABLE[i]), [open]);

    const scrollBy =(amount) => bufferRef.current?.scrollBy({ top: amount, behavior: 'smooth' });

    const run = (input) => {
        const [name, ...rest] = input.trim().split(/\s+/);
        const arg = rest.join(' ');
        switch (name) {
            case '':
                return '';
            case 'q':
            case 'q!':
            case 'qa':
            case 'wq':
            case 'x':
                onQuit();
                return '';
            case 'e':
            case 'edit':
            case 'o':
            case 'open': {
                if (!arg) return 'E32: No file name';
                const found = findBuffer(arg);
                if (!found) return `E484: Can't open file ${arg}`;
                open(found);
                return '';
            }
            case 'set':
            case 'se': {
                const OPTION = { rnu: 'rnu', relativenumber: 'rnu', crt: 'crt' };
                if (!arg) return `  rnu=${opts.rnu}  crt=${opts.crt}`;
                const m = arg.match(/^(no)?(\w+)(!)?$/);
                const key = m && OPTION[m[2]];
                if (!key) return `E518: Unknown option: ${arg}`;
                const value = m[3] ? !opts[key] : !m[1];
                setOpts((o) => ({ ...o, [key]: value }));
                return `  ${value ? '' : 'no'}${key}`;
            }
            case 'neofetch': {
                const readme = findBuffer('README.md');
                if (buffer === readme) bufferRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                else open(readme);
                return '';
            }
            case 'theme':
            case 'colo':
            case 'colorscheme': {
                if (!arg) {
                    const t = onTheme();
                    return `theme: ${t.name} — ${t.description}`;
                }
                const t = THEMES.find((x) => x.name === arg.toLowerCase());
                if (!t) return `E185: Cannot find color scheme '${arg}'`;
                onTheme(t.name);
                return `theme: ${t.name} — ${t.description}`;
            }
            case 'themes':
                return `themes: ${THEMES.map((t) => t.name).join(' · ')}`;
            case 'h':
            case 'help':
                open(findBuffer('help.txt'));
                return '';
            case 'w':
            case 'write':
                return `"${buffer.path}" is read-only — try :q`;
            case 'mail':
            case 'contact':
                window.location.href = `mailto:${CONTACT.email}`;
                return `opening mail to ${CONTACT.email}`;
            default: {
                if (/^\d+$/.test(name)) {
                    const row = bufferRef.current?.querySelectorAll('.ln')[parseInt(name, 10) - 1];
                    row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    return '';
                }
                return `E492: Not an editor command: ${input.trim()}`;
            }
        }
    };

    useEffect(() => {
        const onKey = (e) => {
            if (cmd !== null) return;
            const tag = e.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            const el = bufferRef.current;
            const half = el ? el.clientHeight / 2 : 300;

            if (e.ctrlKey && (e.key === 'd' || e.key === 'u')) {
                e.preventDefault();
                scrollBy(e.key === 'd' ? half : -half);
                return;
            }
            if (e.metaKey || e.ctrlKey || e.altKey) return;

            switch (e.key) {
                case 'j':
                case 'ArrowDown':
                    if (e.key === 'ArrowDown' && e.target === el) return;
                    e.preventDefault();
                    step(1);
                    break;
                case 'k':
                case 'ArrowUp':
                    if (e.key === 'ArrowUp' && e.target === el) return;
                    e.preventDefault();
                    step(-1);
                    break;
                case 'g':
                    if (Date.now() - pendingG.current < 500) {
                        el?.scrollTo({ top: 0, behavior: 'smooth' });
                        pendingG.current = 0;
                    } else {
                        pendingG.current = Date.now();
                    }
                    break;
                case 'G':
                    el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                    break;
                case ':':
                    e.preventDefault();
                    setMessage('');
                    setCmd('');
                    break;
                case 'Escape':
                    setTreeOpen(false);
                    setMessage('');
                    break;
                default:
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [cmd, step]);

    const onCmdKey = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            setCmd(null);
        } else if (e.key === 'Tab') {
            e.preventDefault();
            setCmd(complete(cmd));
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const h = history.current;
            if (!h.length) return;
            const at = e.key === 'ArrowUp' ? Math.max(0, (histAt.current ?? h.length) - 1) : Math.min(h.length, (histAt.current ?? h.length) + 1);
            histAt.current = at;
            setCmd(h[at] ?? '');
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (cmd.trim()) history.current = [...history.current.filter((c) => c !== cmd), cmd].slice(-30);
            histAt.current = null;
            const result = run(cmd);
            setCmd(null);
            setMessage(result);
        } else if (e.key === 'Backspace' && cmd === '') {
            setCmd(null);
        }
    };

    const isProject = buffer.path.startsWith('projects/');
    const mode = cmd !== null ? 'COMMAND' : 'NORMAL';

    return (
        <main className={`editor-stage ${opts.crt ? 'is-crt' : ''}`}>
            <h1 className="sr-only">Adeboye Bello — {buffer.title}</h1>
            <header className="editor-top">
                <a className="brand" href="#/" onClick={(e) => { e.preventDefault(); onQuit(); }} title="back to the intro">
                    <span className="brand-mark" aria-hidden="true">ab</span>
                    <span>adeboye.bello</span>
                </a>
                <nav className="intro-links" aria-label="Elsewhere">
                    {SOCIALS.slice(0, 2).map((s) => (
                        <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer">{s.name}</a>
                    ))}
                    <a href={`mailto:${CONTACT.email}`}>email</a>
                </nav>
            </header>

            <section className={`editor ${treeOpen ? 'tree-is-open' : ''}`} aria-label="Editor">
                <button
                    type="button"
                    className="tabline"
                    aria-expanded={treeOpen}
                    aria-controls="tree"
                    onClick={() => setTreeOpen((v) => !v)}
                >
                    <span className="tab-dir">~/adeboye/</span>
                    <span className="tab-file">{buffer.path}</span>
                    <span className="tab-toggle" aria-hidden="true">{treeOpen ? '↑' : '↓'}</span>
                </button>

                <nav className="side" aria-label="Files">
                    <Wheel items={OPENABLE} index={wheelIndex} onSelect={selectIndex} />
                </nav>

                <nav className="tree" id="tree" aria-label="Files">
                    <Tree current={buffer} onOpen={open} />
                </nav>

                <div className="pane">
                    <p className="winbar">
                        {buffer.lang === 'term' ? (
                            <>zsh <span className="dimmed">—</span> ~ <span className="dimmed">·</span> adeboye-bello</>
                        ) : (
                            <>
                                <span className="dimmed">{isProject ? `${buffer.path.split('/')[0]}/ — ` : '~/ — '}</span>
                                {buffer.name}
                            </>
                        )}
                    </p>
                    <Globe />
                    <Buffer key={buffer.path} rnu={opts.rnu} buffer={buffer} ref={bufferRef} onScroll={(e) => setPos(position(e.currentTarget))} />
                </div>
            </section>

            <p className={`statusline ${mode === 'COMMAND' ? 'is-command' : ''}`}>
                <span className="mode">{mode}</span>
                <span className="branch" aria-hidden="true"> main </span>
                <span className="path">{buffer.path}</span>
                <span className="meta">
                    {FILETYPES[buffer.lang] || 'text'} · {buffer.lines.length}L · {pos}
                </span>
            </p>

            <div className="cmdline">
                {cmd !== null ? (
                    <label className="cmd-input">
                        <span aria-hidden="true">:</span>
                        <input
                            ref={inputRef}
                            value={cmd}
                            onChange={(e) => setCmd(e.target.value)}
                            onKeyDown={onCmdKey}
                            onBlur={() => setCmd(null)}
                            aria-label="Command"
                            spellCheck={false}
                            autoComplete="off"
                            autoCapitalize="off"
                        />
                    </label>
                ) : (
                    <button type="button" className={`cmd-msg ${message.startsWith('E') ? 'is-error' : ''}`} onClick={() => setCmd('')}>
                        {message || <span className="dimmed">type :help for commands</span>}
                    </button>
                )}
            </div>

            <div className="bar">
                <p className="bar-id">
                    <span className="bar-title">ADEBOYE-BELLO</span>
                    <span className="bar-sub">↑↓ file · gg/G scroll · : cmd</span>
                </p>
                <p className="col"><span className="label">PROJECTS</span><span className="value is-hot">{STATS.projects}</span></p>
                <p className="col"><span className="label">LIVE</span><span className="value is-hot">{STATS.live}</span></p>
                <p className="col hide-sm"><span className="label">SHIPPING</span><span className="value">{STATS.years}+ yrs</span></p>
                <p className="col hide-sm"><span className="label">TOOLS</span><span className="value">{STATS.tools}</span></p>
                <p className="col">
                    <span className="label">THEME</span>
                    <button type="button" className="value is-hot theme-cycle" onClick={() => onTheme()} aria-label={`Theme: ${theme.name}. Change theme`}>
                        {theme.name}
                    </button>
                </p>
                <Perf />
                <p className="col hide-sm bar-end">
                    <span className="label">ABUJA · WAT</span>
                    <span className="value">{time}</span>
                </p>
            </div>

            <Keys />
        </main>
    );
}
